// Builds an AntD column from a catalogue metric. The page keeps everything
// that is its own (search, filters, chips, special renders, the sorter and
// its null sentinel); the catalogue supplies the label, the width share and the default render for the value kind.
import React from "react";
import { TableColumnType } from "antd";
import { th, tnum } from "shared/components/TableText";
import { translate } from "shared/languages";
import { formatCurrency } from "shared/utils";
import {
  ColumnKind,
  KIND_SPEC,
  REPORT_METRICS,
  ReportMetric,
  formatDateOrDash,
  formatDateTime,
  shareLayout,
} from "./columnCatalogue";

export type ReportColumn<Row> = TableColumnType<Row> & {
  reportKind: ColumnKind;
  reportShare: number | null;
  reportMin: number;
};

// A sortable column needs a dataIndex: useTableSort reads AntD's
// `sorter.field`, which is the dataIndex, so a key-only column could never
// mirror its sort into the PDF caption.
type Sortable<Row> = {
  dataIndex: string;
  key?: undefined;
  sorter?: (a: Row, b: Row) => number;
  defaultSortOrder?: "ascend" | "descend";
};
type KeyOnly = {
  key: string;
  dataIndex?: undefined;
  sorter?: undefined;
  defaultSortOrder?: undefined;
};

export type ReportColumnOptions<Row> = (Sortable<Row> | KeyOnly) & {
  // Thunks, so a namespace-dependent or composed header resolves per render.
  label?: () => string;
  info?: () => string | undefined;
  // Rare overrides of the metric's own kind / width.
  kind?: ColumnKind;
  share?: number;
  min?: number;
  render?: TableColumnType<Row>["render"];
  // The getColumnSearchProps(...) result. Spread LAST, like the pages always
  // did; it carries only filterDropdown / filterIcon / onFilter.
  search?: TableColumnType<Row>;
  // Page-owned column props: filters, onFilter, filteredValue, ellipsis.
  extra?: Partial<TableColumnType<Row>>;
};

const bold = (value: unknown) => <span style={{ fontWeight: 600 }}>{String(value ?? "")}</span>;
const num = (value: unknown) => <span style={tnum}>{String(value ?? "")}</span>;

const defaultRender = <Row,>(kind: ColumnKind): TableColumnType<Row>["render"] => {
  switch (kind) {
    case "name":
      return (value: unknown) => bold(value);
    case "count":
    case "code":
      return (value: unknown) => num(value);
    // A missing amount or rate is "-", never a silent zero; a real 0 still
    // prints as Rp 0 / 0%. Pages with their own rounding keep their render.
    case "money":
      return (value: unknown) =>
        num(value === null || value === undefined ? "-" : formatCurrency(Number(value)));
    case "percent":
      return (value: unknown) => num(value === null || value === undefined ? "-" : `${value}%`);
    // Dates in tabular digits too, so the column lines up like the counts.
    case "date":
      return (value: unknown) => num(formatDateOrDash(value as string | null | undefined));
    case "datetime":
      return (value: unknown) => num(value ? formatDateTime(String(value)) : "-");
    // A text cell is wrapped in an element so the content layout can measure
    // it and cap its width (.report-cell-wrap acts on the cell's first
    // child; a bare text node has no box of its own).
    case "text":
    case "twoLine":
      return (value: unknown) => <span>{String(value ?? "")}</span>;
    default:
      return undefined;
  }
};

export const reportColumn = <Row extends object>(
  metric: ReportMetric,
  o: ReportColumnOptions<Row>,
): ReportColumn<Row> => {
  const spec = REPORT_METRICS[metric];
  const kind = o.kind ?? spec.kind;
  const k = KIND_SPEC[kind];
  const label = o.label ? o.label() : translate(spec.label);
  const info = o.info ? o.info() : spec.info ? translate(spec.info) : undefined;
  const identity: Partial<TableColumnType<Row>> =
    o.dataIndex !== undefined ? { dataIndex: o.dataIndex } : { key: o.key };
  return {
    title: th(label, info),
    ...identity,
    render: o.render ?? defaultRender<Row>(kind),
    ...(o.sorter ? { sorter: o.sorter } : {}),
    ...(o.defaultSortOrder ? { defaultSortOrder: o.defaultSortOrder } : {}),
    ...(o.extra ?? {}),
    ...(o.search ?? {}),
    reportKind: kind,
    reportShare: o.share ?? k.share,
    reportMin: o.min ?? k.min,
  };
};

// How a report table divides its width.
//
// "content" (the Laporan default since 2026-10-16): every column is as wide
// as its own header and widest value, the browser's auto table layout does
// the measuring, spare width is spread across the columns so a short table
// still fills its card, and the table scrolls sideways (first column pinned)
// only when the one-line columns together outgrow the window. Headers never
// wrap; only name and text cells may, inside a width cap. This replaced the
// percentage shares, which gave a column its width by KIND and so squeezed
// "Completed Sessions" into the same 9% as "Sold" and wrapped it.
//
// "shares": the percentage shares from the catalogue (the PDF's own math),
// kept for the session-detail modal, whose ellipsis columns need a fixed
// layout.
export type ReportTableLayout = "content" | "shares";

// Grid widths, in layout px, one per kind: every count column the same
// width, every money column the same, and so on, so a report reads as a
// regular grid (Rezerv's reports do this with one width for every column;
// per kind keeps a "3" from being padded out to an amount's width). Under
// the auto layout a width is a floor: a header or value that needs more
// still grows its column; wrapping kinds (name, text) are left to their
// content width so a name and its chip stay on one line. ReportTable
// applies them only while the grid
// fits the window; otherwise the columns fall back to their content widths
// before any sideways scroll appears, so a small studio PC never scrolls
// more than it would without the grid.
export const GRID_WIDTH: Record<ColumnKind, number> = {
  name: 300,
  text: 240,
  count: 150,
  percent: 170,
  duration: 190,
  money: 190,
  date: 160,
  datetime: 180,
  daterange: 200,
  chip: 150,
  code: 150,
  twoLine: 180,
};

// Comfortable minimums for a table that has to scroll anyway: a little under
// the grid widths. Without them an overflowing table sat every column at its
// bare minimum, "TRX-000902" against "Rp 1.000.000" with only the gutter
// between, while a fitting report had slack spread everywhere. A wrapping
// column takes the larger of its floor and its measured content (as on the
// grid), so the pinned name column keeps one width from page to page instead
// of shrinking to a page of short names.
export const COMFORT_WIDTH: Partial<Record<ColumnKind, number>> = {
  name: 240,
  text: 200,
  twoLine: 180,
  count: 120,
  percent: 140,
  duration: 160,
  money: 160,
  date: 140,
  datetime: 165,
  daterange: 185,
  chip: 130,
  code: 135,
};

// Kinds whose cells may wrap. Everything else (numbers, money, dates,
// codes, chips) stays on one line, so a column never grows a row.
export const WRAPPING_KINDS: ColumnKind[] = ["name", "text", "twoLine"];

// Width cap, in layout px, for a wrapping cell's content. Below it a name
// stays on one line; a longer one breaks onto a second line instead of
// widening the column for every row. CSS max-width is ignored on table
// cells, so the cap goes on the cell's content (see .report-cell-wrap).
export const WRAP_MAX_WIDTH: Partial<Record<ColumnKind, number>> = {
  name: 400,
  text: 280,
  twoLine: 280,
};

// A first column of one of these kinds is the row's identity and is pinned.
// Loyalty, Referral and Pemakaian Reward lead with a date, which is not.
const PINNABLE_KINDS: ColumnKind[] = ["name", "text"];

const wrapCellProps = (kind: ColumnKind) => ({
  className: "report-cell-wrap",
  style: { "--report-wrap-max": `${WRAP_MAX_WIDTH[kind] ?? 280}px` } as React.CSSProperties,
});

// Turns report columns into plain AntD columns for the chosen layout.
//
// content: no widths at all (the browser sizes them); the first column is
// pinned when it is the row's identity (a name or text column, never a
// date), so it stays in view while the rest scrolls; wrapping kinds get the
// cap class on their body cells.
//
// shares: percentage widths from shareLayout and the table's minimum width
// (the one width-less column, the name, takes the remainder).
// How the content layout is sizing its columns right now, as ReportTable's
// hook judges it: "grid" (per-kind widths, the table fits), "content" (bare
// content widths, the table fits that way), "squeeze" (the wrapping columns
// are held below their natural widths, down to their floors, so their names
// wrap and the table still fits), "comfort" (the table scrolls either way,
// so every kind gets its COMFORT_WIDTH floor). Plus the measured natural
// width of each wrapping column (by column index), so a name column on the
// grid is never narrower than "name + chip", and the squeezed widths.
export type ReportLayoutMode = "grid" | "content" | "squeeze" | "comfort";
export type GridState = {
  mode: ReportLayoutMode;
  wrapWidths: Record<number, number>;
  squeezeWidths: Record<number, number>;
};

export const layoutReportColumns = <Row extends object>(
  cols: ReportColumn<Row>[],
  layout: ReportTableLayout = "content",
  // content layout only: the per-kind grid, when it is on.
  grid: GridState = { mode: "content", wrapWidths: {}, squeezeWidths: {} },
): { columns: TableColumnType<Row>[]; scrollX: number | "max-content" } => {
  if (layout === "content") {
    const columns = cols.map(({ reportKind, reportShare, reportMin, ...col }, i) => ({
      ...col,
      // On the grid every column carries a width, so the browser spreads
      // spare width over all of them in proportion (a width-less column
      // would take all of it and leave one very long first column on a
      // wide monitor). A one-line kind takes its grid width; a wrapping
      // kind takes the larger of its grid width and its measured content,
      // because a width below the content would make the browser wrap it
      // and drop a chip under its name.
      ...(grid.mode === "grid"
        ? {
            width: WRAPPING_KINDS.includes(reportKind)
              ? Math.max(GRID_WIDTH[reportKind], grid.wrapWidths[i] ?? 0)
              : GRID_WIDTH[reportKind],
          }
        : grid.mode === "squeeze" && WRAPPING_KINDS.includes(reportKind) && grid.squeezeWidths[i] !== undefined
        ? { width: grid.squeezeWidths[i] }
        : grid.mode === "comfort" && COMFORT_WIDTH[reportKind]
        ? {
            width: WRAPPING_KINDS.includes(reportKind)
              ? Math.max(COMFORT_WIDTH[reportKind] as number, grid.wrapWidths[i] ?? 0)
              : COMFORT_WIDTH[reportKind],
          }
        : {}),
      ...(i === 0 && PINNABLE_KINDS.includes(reportKind) ? { fixed: "left" as const } : {}),
      ...(WRAPPING_KINDS.includes(reportKind)
        ? {
            onCell: (record: Row, index?: number) => ({
              ...(col.onCell ? col.onCell(record, index) : {}),
              ...wrapCellProps(reportKind),
            }),
          }
        : {}),
    }));
    return { columns, scrollX: "max-content" };
  }
  const shares = shareLayout(cols.map((col) => col.reportShare));
  const scrollX = cols.reduce((total, col) => total + col.reportMin, 0);
  const columns = cols.map(({ reportKind, reportShare, reportMin, ...col }, i) =>
    reportShare === null ? col : { ...col, width: `${shares[i]}%` },
  );
  return { columns, scrollX };
};
